import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { t, vakMsg } from '../../../../common/i18n';
import { scrollTo } from '../../../../common/utils/browserUtils';
import { DeliveryType } from '../../../../generated/api/deliveryType';
import { POBoxWarningModal } from '../../../Modal/POBoxWarningModal';
import { COLLECT_POINT_TYPES, DeliveryModeType } from '../../placeOrderTypes';
import {
  type MergedDeliveryMethod,
  hasBackorderedItems,
  hasDirectDeliveryItems,
  hasNothingToDeliver,
  hasOnlySubscriptionItems,
  hasPreInstallation,
  hasVakProductItems,
  isPOBoxDelivery,
  shouldShowShipmentType,
} from '../../utils/placeOrderUtils';
import { StepError } from '../StepError';
import { StepButtons } from '../common/StepButtons';
import { StepContent } from '../common/StepContent';
import { AddressSelection } from './AddressSelection';
import { DeliveryMethodList } from './DeliveryMethodList';
import { TotalOrPartialDeliveryButtons } from './TotalOrPartialDeliveryButtons';
import type { CompanyInfoResponse } from '../../../../generated/api/companyInfoResponse';
import type { AuthenticatedUser } from '../../../../public/site/authState';
import type { PlaceOrderProps } from '../../PlaceOrder';
import type { PlaceOrderFormData } from '../../placeOrderTypes';

export interface DeliveryStepProps extends PlaceOrderProps {
  onNext: () => void;
  onPrevious: () => void;
  company: CompanyInfoResponse;
  deliveryMethods: MergedDeliveryMethod[];
  user: AuthenticatedUser;
  orderSummary: JSX.Element;
}

export const DeliveryStep = ({
  onNext,
  onPrevious,
  company,
  deliveryMethods,
  user,
  shoppingBasket,
  onlineModels,
  orderSummary,
}: DeliveryStepProps) => {
  const { watch, setValue } = useFormContext<PlaceOrderFormData>();
  const [addressError, setAddressError] = useState<string | undefined>();
  const [shipmentError, setShipmentError] = useState<string | undefined>();
  const [deliveryMethodError, setDeliveryMethodError] = useState<string | undefined>();
  const [showPOBoxWarningModal, setShowPOBoxWarningModal] = useState(false);

  const delivery = watch('delivery');
  const products = watch('products');
  const formData = { delivery, products, payment: {} } as PlaceOrderFormData;
  const nothingToDeliver = hasNothingToDeliver(shoppingBasket, onlineModels, formData);
  const isSubscriptionOnly = hasOnlySubscriptionItems(shoppingBasket, onlineModels);
  const showShipmentType = shouldShowShipmentType(shoppingBasket, onlineModels);
  const isDirectDelivery = hasDirectDeliveryItems(shoppingBasket, onlineModels);
  const hasBackordered = hasBackorderedItems(shoppingBasket, onlineModels);
  const preInstallation = hasPreInstallation(shoppingBasket);
  const hasVakProduct = hasVakProductItems(shoppingBasket, onlineModels);
  const selectedDeliveryMode = isSubscriptionOnly
    ? DeliveryModeType.ADDRESS
    : deliveryMethods.find(method => method.deliveryTypeCode === delivery.deliveryType)?.mode;
  const allowedModes = hasVakProduct
    ? [DeliveryModeType.ADDRESS]
    : [DeliveryModeType.ADDRESS, DeliveryModeType.COLLECT_POINT];

  // Fill pickup-point postal code search when delivery address changes
  useEffect(() => {
    if (delivery.deliveryAddress?.address?.postalCode) {
      setValue('delivery.pickupPointPostalCode', delivery.deliveryAddress.address.postalCode);
    }
  }, [delivery.deliveryAddress?.address?.postalCode, setValue]);

  // Set delivery type based on order contents
  useEffect(() => {
    if (nothingToDeliver) {
      setValue('delivery.deliveryType', DeliveryType.NONE);
    } else if (isSubscriptionOnly) {
      setValue('delivery.deliveryType', DeliveryType.LETTER);
    }
  }, [nothingToDeliver, isSubscriptionOnly, delivery.deliveryType, setValue]);

  const validateDelivery = () => {
    // Skip validation when nothing to deliver
    if (nothingToDeliver) {
      return true;
    }

    let isValid = true;

    if (!delivery.deliveryAddress) {
      setAddressError(t.V1VL('Address information is missing'));
      isValid = false;
    }

    if (showShipmentType && !delivery.shipmentType) {
      setShipmentError(t.OOXW('Shipment type is missing'));
      isValid = false;
    }

    // Skip delivery method validation for subscription-only orders (delivered by letter)
    if (!isSubscriptionOnly) {
      if (!delivery.deliveryType) {
        setDeliveryMethodError(t.OEB7('Delivery method is missing'));
        isValid = false;
      } else {
        const isCollectPoint = COLLECT_POINT_TYPES.includes(delivery.deliveryType);
        if (isCollectPoint && (!delivery.pickupPointPostalCode || delivery.pickupPointPostalCode.length !== 5)) {
          setDeliveryMethodError(t.RXWZ('Postal code is required for pickup point selection'));
          isValid = false;
        } else if (isCollectPoint && !delivery.pickupPoint) {
          setDeliveryMethodError(t.MZ1U('Pickup point selection is required'));
          isValid = false;
        }
      }
    }

    return isValid;
  };

  const handleNext = async () => {
    if (validateDelivery()) {
      if (!nothingToDeliver && isPOBoxDelivery(delivery.deliveryAddress?.address.postalCode, selectedDeliveryMode)) {
        setShowPOBoxWarningModal(true);
      } else {
        onNext();
      }
    }
    scrollTo(0);
  };

  return (
    <div className="of-place-order__delivery-step">
      {showPOBoxWarningModal && <POBoxWarningModal setShowModal={setShowPOBoxWarningModal} />}
      <h1>{t.BSE3('Delivery')}</h1>
      {nothingToDeliver ? (
        <>
          <CL.Disclaimer
            className={dsClass.MARGIN_BOTTOM_4}
            disclaimerType="info"
            icon={<CL.Icon icon="information" />}
            text={t.FPAP('Your order includes products that do not require delivery.')}
          />
          {orderSummary}
          <StepButtons onNext={handleNext} onPrevious={onPrevious} />
        </>
      ) : (
        <>
          <StepError
            errors={[
              ...(shipmentError ? [{ text: shipmentError, id: 'partial-shipment-id' }] : []),
              ...(addressError ? [{ text: addressError, id: 'address-selection-id' }] : []),
              ...(deliveryMethodError ? [{ text: deliveryMethodError, id: 'delivery-method-id' }] : []),
            ]}
          />
          {showShipmentType && (
            <StepContent id="partial-shipment-id" errorText={shipmentError}>
              <TotalOrPartialDeliveryButtons
                isDirectDelivery={isDirectDelivery}
                hasBackorderedItems={hasBackordered}
                hasPreInstallation={preInstallation}
                onErrorChange={() => setShipmentError(undefined)}
              />
            </StepContent>
          )}
          <StepContent id="address-selection-id" errorText={addressError}>
            <AddressSelection user={user} company={company} onClearError={() => setAddressError(undefined)} />
          </StepContent>
          {!isSubscriptionOnly && (
            <StepContent id="delivery-method-id" errorText={deliveryMethodError}>
              {hasVakProduct && (
                <CL.Disclaimer
                  className={dsClass.MARGIN_BOTTOM_2}
                  disclaimerType="warning"
                  icon={<CL.Icon color="yellow" icon="notification" />}
                  text={t.XWA9(vakMsg)}
                />
              )}
              <DeliveryMethodList
                deliveryMethods={deliveryMethods}
                allowedModes={allowedModes}
                onClearError={() => setDeliveryMethodError(undefined)}
              />
            </StepContent>
          )}
          {orderSummary}
          <StepButtons onNext={handleNext} onPrevious={onPrevious} />
        </>
      )}
    </div>
  );
};
